import { useEffect, type ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { t } from '@/lib/i18n'

/**
 * 底部 sheet。取代居中 modal、右上角 dropdown 与 window.prompt/confirm。
 *
 * 为什么不是居中弹窗：手机上居中要伸手到屏幕中间（拇指够不着），而且一聚焦输入框
 * 就会被软键盘顶掉。底部 sheet 拇指可达，键盘顶上来时它正好贴着键盘。
 *
 * **常驻挂载**而不是 open 时才渲染：滑入/滑出的 transition 需要元素在 DOM 里先存在
 * 一帧（同一帧 mount + 加 class 不会触发过渡，会直接跳到终态）。代价是关闭时它的
 * 内容仍在 DOM 里可被 Tab 聚焦，所以关闭时挂 inert（React 19 认这个布尔属性）。
 *
 * z 层：遮罩 70 / 本体 71。庆祝卡与转盘在 80，刻意压在 sheet 之上 ——
 * 它们是「要求注意」的东西，而 sheet 可能正开着（比如刚在 sheet 里加完步骤就达成了目标）。
 */
export function Sheet({ open, onClose, label, title, children }: {
  open: boolean
  onClose: () => void
  /** aria-label。确认类 sheet 可能没有可见标题，但一定要有名字。 */
  label: string
  title?: string
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)

    // 锁住背景滚动。body 上有 overscroll-behavior-y:contain 兜着橡皮筋，但滚动本身
    // 还在：sheet 里滑到底之后手势会传给背景，页面在身后偷偷滚掉。
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  return (
    <>
      {/* 遮罩。aria-hidden：它不是一个可聚焦的动作，键盘路径是 Escape（外加下面那个
          sr-only 的关闭按钮）。 */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={
          'fixed inset-0 z-[70] bg-black/40 transition-opacity duration-250 ' +
          (open ? 'opacity-100' : 'opacity-0 pointer-events-none')
        }
      />
      <div
        role="dialog"
        aria-modal={open}
        aria-label={label}
        inert={!open}
        className={
          'fixed left-0 right-0 bottom-0 z-[71] bg-card rounded-t-card pt-2 pb-sheet ' +
          'max-h-[86vh] overflow-y-auto transition-transform duration-300 ease-sheet ' +
          (open ? 'translate-y-0' : 'translate-y-full')
        }
      >
        {/* 抓手：不做拖拽手势（零新增依赖），它只是「这东西是从下面拉上来的」这个提示。 */}
        <div className="w-[38px] h-1 rounded-full bg-line mx-auto mt-1.5 mb-2.5" />
        {title && <h3 className="m-0 px-5 pt-0.5 pb-2.5 text-base font-bold">{title}</h3>}
        {children}
        {/* 关闭按钮不画进 sheet 里 —— 稿子七份都没有：遮罩点一下就关，Escape 也关。
            但屏幕阅读器需要一个真的可聚焦的出口。 */}
        <button type="button" onClick={onClose} className="sr-only">
          {t('layout.close')}
        </button>
      </div>
    </>
  )
}

/** 稿子的 `.srow`：sheet 里的一行。52px 高，左图标 / 中文案 / 右 tail。 */
export function SheetRow({ icon, tail, tone = 'default', onClick, href, children }: {
  icon?: string
  tail?: ReactNode
  tone?: 'default' | 'danger'
  onClick?: () => void
  href?: string
  children: ReactNode
}) {
  const cls =
    'flex items-center gap-3 w-full min-h-[52px] px-5 text-left text-[15px] active:bg-sunk ' +
    (tone === 'danger' ? 'text-red-strong' : '')

  const body = (
    <>
      {icon && <i className="not-italic text-[19px] w-6 text-center shrink-0">{icon}</i>}
      <span className="flex-1 min-w-0">{children}</span>
      {tail && <span className="text-faint text-[13px] shrink-0 truncate max-w-[45%]">{tail}</span>}
    </>
  )

  if (href) return <Link href={href} onClick={onClick} className={cls}>{body}</Link>
  return <button type="button" onClick={onClick} className={cls}>{body}</button>
}

/** 稿子的 `.pad`：sheet 里放表单与按钮的左右内边距。`.srow` 自带 px-5，表单没有。 */
export function SheetPad({ children }: { children: ReactNode }) {
  return <div className="px-5">{children}</div>
}

/** 稿子的 `<hr>`：sheet 里的分组分隔线。刻意不用「分组标题」——手机上一行标题
    换来的信息量不如少一行。 */
export function SheetDivider() {
  return <hr className="border-0 border-t border-line my-2" />
}
